import React, { useEffect, useState } from 'react';
import {
    FiCheck,
    FiDownload,
    FiEdit2,
    FiMail,
    FiPlus,
    FiRefreshCw,
    FiSearch,
    FiTrash2,
    FiUsers,
    FiX
} from 'react-icons/fi';
import apiService from '../services/api';

const Subscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    source: 'all',
    engagement: 'all'
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 0,
    total: 0,
    limit: 10
  });
  
  // Selection and actions
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkActionType, setBulkActionType] = useState('');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingSubscriber, setDeletingSubscriber] = useState(null);

  useEffect(() => {
    fetchSubscribers();
    fetchStats();
  }, [filters, pagination.current, pagination.limit]);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.limit,
        ...filters
      };
      
      // Remove 'all' values
      Object.keys(params).forEach(key => {
        if (params[key] === 'all') delete params[key];
      });

      const response = await apiService.getSubscribers(params);
      setSubscribers(response.data.subscribers);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.getSubscriberStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleSelectAll = () => {
    if (selectedSubscribers.length === subscribers.length) {
      setSelectedSubscribers([]);
    } else {
      setSelectedSubscribers(subscribers.map(sub => sub._id));
    }
  };

  const handleSelectSubscriber = (id) => {
    if (selectedSubscribers.includes(id)) {
      setSelectedSubscribers(prev => prev.filter(subId => subId !== id));
    } else {
      setSelectedSubscribers(prev => [...prev, id]);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkActionType || selectedSubscribers.length === 0) return;

    try {
      let actionData = {};
      
      switch (bulkActionType) {
        case 'delete':
          await apiService.bulkSubscriberOperation({
            action: 'delete',
            ids: selectedSubscribers
          });
          break;
        case 'activate':
          actionData = { status: 'active' };
          await apiService.bulkSubscriberOperation({
            action: 'updateStatus',
            ids: selectedSubscribers,
            data: actionData
          });
          break;
        case 'deactivate':
          actionData = { status: 'inactive' };
          await apiService.bulkSubscriberOperation({
            action: 'updateStatus',
            ids: selectedSubscribers,
            data: actionData
          });
          break;
        default:
          return;
      }

      setSelectedSubscribers([]);
      setShowBulkActions(false);
      setBulkActionType('');
      fetchSubscribers();
      fetchStats();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await apiService.exportSubscribers(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'subscribers.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to export subscribers');
    }
  };

  const handleAddSubscriber = async (formData) => {
    try {
      await apiService.createSubscriber(formData);
      setShowAddModal(false);
      fetchSubscribers();
      fetchStats();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditSubscriber = async (formData) => {
    try {
      await apiService.updateSubscriber(editingSubscriber._id, formData);
      setShowEditModal(false);
      setEditingSubscriber(null);
      fetchSubscribers();
      fetchStats();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteSubscriber = async () => {
    try {
      await apiService.deleteSubscriber(deletingSubscriber._id);
      setShowDeleteModal(false);
      setDeletingSubscriber(null);
      fetchSubscribers();
      fetchStats();
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-500/20 text-green-400 border-green-400/30',
      inactive: 'bg-gray-500/20 text-gray-400 border-gray-400/30',
      bounced: 'bg-red-500/20 text-red-400 border-red-400/30',
      unsubscribed: 'bg-orange-500/20 text-orange-400 border-orange-400/30'
    };
    return colors[status] || colors.inactive;
  };

  const getSourceColor = (source) => {
    const colors = {
      Website: 'bg-blue-500/20 text-blue-400',
      LinkedIn: 'bg-indigo-500/20 text-indigo-400',
      Newsletter: 'bg-purple-500/20 text-purple-400',
      Referral: 'bg-green-500/20 text-green-400',
      'Contact Form': 'bg-cyan-500/20 text-cyan-400',
      Other: 'bg-gray-500/20 text-gray-400'
    };
    return colors[source] || colors.Other;
  };

  const getEngagementColor = (engagement) => {
    const colors = {
      high: 'bg-green-500/20 text-green-400',
      medium: 'bg-yellow-500/20 text-yellow-400',
      low: 'bg-orange-500/20 text-orange-400',
      none: 'bg-gray-500/20 text-gray-400'
    };
    return colors[engagement] || colors.none;
  };

  if (loading && subscribers.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <span className="text-white text-lg">Loading subscribers...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text text-transparent font-display mb-2">
                Newsletter Subscribers
              </h1>
              <p className="text-slate-300 text-lg">
                Manage your newsletter subscribers and engagement
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 rounded-lg text-emerald-400 text-sm transition-all duration-300 flex items-center gap-2 hover:scale-105"
              >
                <FiDownload className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/30 rounded-lg text-cyan-400 text-sm transition-all duration-300 flex items-center gap-2 hover:scale-105"
              >
                <FiPlus className="w-4 h-4" />
                Add Subscriber
              </button>
              <button
                onClick={fetchSubscribers}
                className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 rounded-lg text-purple-400 text-sm transition-all duration-300 flex items-center gap-2 hover:scale-105"
              >
                <FiRefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-500/20 border border-red-400/30 rounded-lg flex items-center justify-between text-red-400">
              <span>{error}</span>
              <button onClick={() => setError(null)}>
                <FiX className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <FiUsers className="w-8 h-8 text-cyan-400" />
                <span className="text-3xl font-bold text-white">{stats.totals?.total || 0}</span>
              </div>
              <h3 className="text-slate-300 text-sm font-medium">Total Subscribers</h3>
            </div>
            
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <FiCheck className="w-8 h-8 text-green-400" />
                <span className="text-3xl font-bold text-white">{stats.totals?.active || 0}</span>
              </div>
              <h3 className="text-slate-300 text-sm font-medium">Active Subscribers</h3>
            </div>
            
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <FiMail className="w-8 h-8 text-purple-400" />
                <span className="text-3xl font-bold text-white">{stats.totals?.growth?.thirtyDays || 0}</span>
              </div>
              <h3 className="text-slate-300 text-sm font-medium">New This Month</h3>
            </div>
            
            <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <div className={`px-2 py-1 rounded-full text-xs ${
                  stats.totals?.growth?.rate >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {stats.totals?.growth?.rate >= 0 ? '+' : ''}{stats.totals?.growth?.rate || 0}%
                </div>
              </div>
              <h3 className="text-slate-300 text-sm font-medium">Growth Rate</h3>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search subscribers by email or name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-400/30"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="bounced">Bounced</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>

            {/* Source Filter */}
            <select
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
            >
              <option value="all">All Sources</option>
              <option value="Website">Website</option>
              <option value="LinkedIn">LinkedIn</option>
              <option value="Newsletter">Newsletter</option>
              <option value="Referral">Referral</option>
              <option value="Contact Form">Contact Form</option>
              <option value="Other">Other</option>
            </select>

            {/* Engagement Filter */}
            <select
              value={filters.engagement}
              onChange={(e) => handleFilterChange('engagement', e.target.value)}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
            >
              <option value="all">All Engagement</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="none">None</option>
            </select>
          </div>

          {/* Bulk Actions */}
          {selectedSubscribers.length > 0 && (
            <div className="mt-4 flex items-center gap-4 p-4 bg-cyan-500/10 border border-cyan-400/30 rounded-lg">
              <span className="text-cyan-400 text-sm">
                {selectedSubscribers.length} subscriber(s) selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setBulkActionType('activate');
                    setShowBulkActions(true);
                  }}
                  className="px-3 py-1 bg-green-500/20 text-green-400 rounded text-xs hover:bg-green-500/30 transition-colors"
                >
                  Activate
                </button>
                <button
                  onClick={() => {
                    setBulkActionType('deactivate');
                    setShowBulkActions(true);
                  }}
                  className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded text-xs hover:bg-orange-500/30 transition-colors"
                >
                  Deactivate
                </button>
                <button
                  onClick={() => {
                    setBulkActionType('delete');
                    setShowBulkActions(true);
                  }}
                  className="px-3 py-1 bg-red-500/20 text-red-400 rounded text-xs hover:bg-red-500/30 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Subscribers Table */}
        <div className="bg-gradient-to-br from-slate-800/80 via-gray-800/80 to-slate-800/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50 border-b border-slate-600/50">
                <tr>
                  <th className="p-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedSubscribers.length === subscribers.length && subscribers.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-slate-600 bg-slate-700 text-cyan-400 focus:ring-cyan-400/30"
                    />
                  </th>
                  <th className="p-4 text-left text-slate-300 font-medium">Subscriber</th>
                  <th className="p-4 text-left text-slate-300 font-medium">Status</th>
                  <th className="p-4 text-left text-slate-300 font-medium">Source</th>
                  <th className="p-4 text-left text-slate-300 font-medium">Engagement</th>
                  <th className="p-4 text-left text-slate-300 font-medium">Subscribed</th>
                  <th className="p-4 text-left text-slate-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-600/30">
                {subscribers.map((subscriber) => (
                  <tr key={subscriber._id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="p-4">
                      <input
                        type="checkbox"
                        checked={selectedSubscribers.includes(subscriber._id)}
                        onChange={() => handleSelectSubscriber(subscriber._id)}
                        className="rounded border-slate-600 bg-slate-700 text-cyan-400 focus:ring-cyan-400/30"
                      />
                    </td>
                    <td className="p-4">
                      <div>
                        <div className="text-white font-medium">{subscriber.name || 'N/A'}</div>
                        <div className="text-slate-400 text-sm">{subscriber.email}</div>
                        {subscriber.location && (
                          <div className="text-slate-500 text-xs">{subscriber.location}</div>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(subscriber.status)}`}>
                        {subscriber.status}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs ${getSourceColor(subscriber.source)}`}>
                        {subscriber.source}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs ${getEngagementColor(subscriber.engagement)}`}>
                        {subscriber.engagement}
                      </span>
                    </td>
                    <td className="p-4 text-slate-400 text-sm">
                      {new Date(subscriber.subscribedAt).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingSubscriber(subscriber);
                            setShowEditModal(true);
                          }}
                          className="p-1 text-slate-400 hover:text-cyan-400 transition-colors"
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setDeletingSubscriber(subscriber);
                            setShowDeleteModal(true);
                          }}
                          className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {subscribers.length === 0 && !loading && (
              <div className="text-center py-12 text-slate-400">
                <FiUsers className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No subscribers found</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="p-4 border-t border-slate-600/30 flex items-center justify-between">
              <div className="text-slate-400 text-sm">
                Showing {((pagination.current - 1) * pagination.limit) + 1} to {Math.min(pagination.current * pagination.limit, pagination.total)} of {pagination.total} subscribers
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                  disabled={!pagination.hasPrev}
                  className="px-3 py-1 bg-slate-700 text-slate-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
                >
                  Previous
                </button>
                <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded">
                  {pagination.current} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                  disabled={!pagination.hasNext}
                  className="px-3 py-1 bg-slate-700 text-slate-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <SubscriberModal
          title="Add New Subscriber"
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddSubscriber}
        />
      )}

      {showEditModal && editingSubscriber && (
        <SubscriberModal
          title="Edit Subscriber"
          subscriber={editingSubscriber}
          onClose={() => {
            setShowEditModal(false);
            setEditingSubscriber(null);
          }}
          onSubmit={handleEditSubscriber}
        />
      )}

      {showDeleteModal && deletingSubscriber && (
        <DeleteModal
          title="Delete Subscriber"
          message={`Are you sure you want to delete ${deletingSubscriber.email}? This action cannot be undone.`}
          onClose={() => {
            setShowDeleteModal(false);
            setDeletingSubscriber(null);
          }}
          onConfirm={handleDeleteSubscriber}
        />
      )}

      {showBulkActions && (
        <BulkActionModal
          action={bulkActionType}
          count={selectedSubscribers.length}
          onClose={() => {
            setShowBulkActions(false);
            setBulkActionType('');
          }}
          onConfirm={handleBulkAction}
        />
      )}
    </div>
  );
};

// Subscriber Modal Component
const SubscriberModal = ({ title, subscriber, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    email: subscriber?.email || '',
    name: subscriber?.name || '',
    status: subscriber?.status || 'active',
    source: subscriber?.source || 'Website',
    engagement: subscriber?.engagement || 'none',
    location: subscriber?.location || '',
    tags: subscriber?.tags?.join(', ') || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const data = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean)
      };
      await onSubmit(data);
    } catch (err) {
      console.error('Form submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-slate-600">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
            />
          </div>

          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="bounced">Bounced</option>
                <option value="unsubscribed">Unsubscribed</option>
              </select>
            </div>

            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">Source</label>
              <select
                value={formData.source}
                onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
              >
                <option value="Website">Website</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Newsletter">Newsletter</option>
                <option value="Referral">Referral</option>
                <option value="Contact Form">Contact Form</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">Engagement</label>
            <select
              value={formData.engagement}
              onChange={(e) => setFormData(prev => ({ ...prev, engagement: e.target.value }))}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="none">None</option>
            </select>
          </div>

          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
            />
          </div>

          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">Tags (comma separated)</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
              placeholder="e.g. newsletter, vip, customer"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Modal Component
const DeleteModal = ({ title, message, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-slate-600">
        <h3 className="text-xl font-bold text-white mb-4">{title}</h3>
        <p className="text-slate-300 mb-6">{message}</p>
        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Bulk Action Modal Component
const BulkActionModal = ({ action, count, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);

  const getActionText = () => {
    switch (action) {
      case 'delete': return 'delete';
      case 'activate': return 'activate';
      case 'deactivate': return 'deactivate';
      default: return action;
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-slate-600">
        <h3 className="text-xl font-bold text-white mb-4">Bulk Action</h3>
        <p className="text-slate-300 mb-6">
          Are you sure you want to {getActionText()} {count} subscriber{count > 1 ? 's' : ''}?
        </p>
        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50 ${
              action === 'delete' ? 'bg-red-500 hover:bg-red-600' : 'bg-cyan-500 hover:bg-cyan-600'
            }`}
          >
            {loading ? 'Processing...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Subscribers; 